import { MiddlewareAPI } from '@reduxjs/toolkit';
import { updateState } from 'core/actions/product/state';
import { validationRequest } from 'core/services/authentication';
import { logout } from 'core/slices/session';
import { ConnectionState, updateConnectionState, recieve, reconnected, send } from 'core/slices/websocket';
import { EntityTypes } from 'constants/entities';
import { MessageModel } from 'types/websocket';
import { TypeSocket } from './typesocket';
import { env, isTesting } from '../../utils/env';
import { JWT_TOKEN, REFRESH_TOKEN } from '../../constants';

/**
 * After this many handshakes in a row fail, the token is probably expired (the
 * websocket's $connect rejects it, and the browser can't tell us it was a 401):
 * refresh it, and the new token restarts the socket.
 */
const REFRESH_AFTER_FAILURES = 2;

const readToken = (): string | null => {
  try {
    return localStorage.getItem(JWT_TOKEN);
  } catch {
    return null;
  }
};

/** Actions after which a (new) token may be in storage. */
const mayCarryToken = (type: string) => type === 'hydrate' || type.endsWith('/fulfilled');

export const socketMiddleware: any = () => {
  return (store: MiddlewareAPI<any, any>) => {
    let usedToken: string | null = null;

    const socket = new TypeSocket<MessageModel>(() => {
      usedToken = readToken();
      return usedToken ? `${env.WS}/socket?token=${usedToken}` : null;
    });

    /** What this tab is looking at; the server routes course/module broadcasts by it. */
    const subscribe = () => {
      const state = store.getState().state ?? {};
      socket.send({
        type: 'subscribe',
        payload: { courseId: state[EntityTypes.COURSE] ?? 0, moduleId: state[EntityTypes.MODULE] ?? 0 },
      });
    };

    const refreshToken = () => {
      try {
        const token = localStorage.getItem(JWT_TOKEN);
        const refresh = localStorage.getItem(REFRESH_TOKEN);
        if (token && refresh) store.dispatch(validationRequest({ token, refresh }) as any);
      } catch {}
    };

    socket.on('connecting', () => store.dispatch(updateConnectionState(ConnectionState.CONNECTING)));

    socket.on('connected', wasReconnect => {
      store.dispatch(updateConnectionState(ConnectionState.CONNECTED));
      subscribe();
      if (wasReconnect) store.dispatch(reconnected());
    });

    socket.on('disconnected', ({ opened, failures }) => {
      store.dispatch(updateConnectionState(ConnectionState.DISCONNECTED));
      if (!opened && failures % 5 === REFRESH_AFTER_FAILURES) refreshToken();
    });

    socket.on('message', message => store.dispatch(recieve(message)));

    return (next: (action: any) => void) => (action: any) => {
      const result = next(action);

      // Under test there is no websocket server; connecting leaves every spec holding a retrying socket.
      if (isTesting() || !action?.type) return result;

      if (action.type === logout.type) {
        socket.stop();
        usedToken = null;
        store.dispatch(updateConnectionState(ConnectionState.PERMENENTLY_DISCONNECTED));
        return result;
      }

      if (mayCarryToken(action.type)) {
        const token = readToken();
        if (token) {
          if (!socket.isStarted) socket.start();
          // A socket authenticates once, at connect; an open one keeps working on its old token.
          else if (!socket.isOpen && token !== usedToken) socket.restart();
        }
      }

      switch (action.type) {
        case send.type:
        case updateState.type:
          socket.send(action);
          break;
      }

      return result;
    };
  };
};
